import { AibLineType, AsupInternalBlock, useArsContext } from "aic";
import { v3EditorProps } from "components/common/v3EditorProps";
import { useSave } from "contexts/appsettings/useSave";
import {
  useAppSettings,
  useHierarchyContext,
  useTlfContext,
  useTocContext,
} from "contexts/AppSettingsContext";
import { getExternalSingles } from "contexts/tlf/getExternalSingles";
import { UPDATE_TLF } from "contexts/tlf/interface";
import { MosaicTocEntryDto } from "contexts/toc/interface";
import { isTitlesFootnotesReadDto } from "functions/api/tlfItem/TitlesFootnotesReadDto";
import { ReactNode } from "react";
import { CommentScope } from "../common/comments/CommentScope";

interface FootnotesProps {
  canAddFirstRow?: boolean;
  noComments?: boolean;
  patch?: boolean;
  tocId?: string;
}

/**
 * Render the footnotes editor block for a TLF item, optionally patched to the server.
 *
 * @param props - Destructured properties for the footnotes component.
 * @param props.canAddFirstRow - When true, allows adding the first row to an empty block.
 * @param props.noComments - When true, disables comment functionality.
 * @param props.patch - When true, persists changes via the TLF patch API.
 * @param props.tocId - Optional identifier for the ToC item associated with the footnotes.
 * @returns A React node containing the footnotes editor or null when context missing.
 */
export const Footnotes = ({
  canAddFirstRow = false,
  noComments = false,
  patch = false,
  tocId,
}: FootnotesProps): ReactNode => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();
  const { state: tocState } = useTocContext();
  const { state: ars } = useArsContext();
  const tlfContext = useTlfContext();
  const { patchTlf, saveRequest } = useSave();
  const tlfData = tocId
    ? (tocState.tocItems.find((tlf) => tlf.id === tocId && isTitlesFootnotesReadDto(tlf)) as
        MosaicTocEntryDto | undefined)
    : tlfContext.tlfData;
  if (!hierarchyContext?.hierarchy || !tlfData || !tlfContext) return null;

  const externalSingles = getExternalSingles(
    undefined,
    ars.analysisSets[tlfData?.analysisSetId ?? ""]?.label ?? "",
    tlfData.releaseStatus,
    tlfData.domain,
    tlfData.singles,
    hierarchyContext.hierarchy.level,
    hierarchyContext.hierarchy.singles,
    hierarchyContext.hierarchy.domainReplacements,
    hierarchyContext.parentTemplates.singles,
    hierarchyContext.parentTemplates.domainReplacements,
  );

  return (
    <CommentScope
      id="footnotes-comment"
      activeContext="TlfItem"
      commentLabel="New footnotes comment"
      disabled={noComments}
    >
      <AsupInternalBlock
        id="footnotes"
        canAddFirstRow={canAddFirstRow}
        lineStyle={{ ...appSettings.defaultTableStyle }}
        lines={tlfData.footnotes}
        disabled={tlfContext.readOnly || (patch && saveRequest.requesting)}
        onChange={(ret) => {
          if (patch) {
            patchTlf(tlfData.id, tlfData.azsolId, "footnotes", ret);
          } else {
            tlfContext.dispatch!({
              operation: UPDATE_TLF,
              tlfData: { footnotes: ret },
              calledFrom: "Footnotes:Update",
            });
          }
        }}
        externalSingles={externalSingles}
        defaultType={AibLineType.leftOnly}
        editorProps={v3EditorProps}
        noComments={noComments}
        noTrafficLights={true}
      />
    </CommentScope>
  );
};

Footnotes.displayName = "Footnotes";
